import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useFormContext, useWatch } from "react-hook-form";
import {
  GAP_MAX,
  GAP_MIN,
  PER_DAY_MAX,
  PER_DAY_MIN,
  useOrganizationCallLimits,
} from "@/lib/callLimits";

interface CallLimitsTabProps {
  // The assistant's organization, whose limits always apply on top of these.
  organizationId?: string;
}

// This assistant's own call limits per phone number (V2 calls). The organization's limits are
// checked as well, so a looser value here has no effect: the organization's value is the ceiling.
export default function CallLimitsTab({ organizationId }: CallLimitsTabProps) {
  const { control } = useFormContext();
  const org = useOrganizationCallLimits(organizationId);
  const perDay = useWatch({ control, name: "max_calls_per_number_per_day" }) as string | undefined;
  const gap = useWatch({ control, name: "min_call_gap_minutes" }) as string | undefined;

  const perDayLooser = org.loaded && !!perDay && Number(perDay) > org.perDay;
  const gapLooser = org.loaded && gap !== undefined && gap !== "" && Number(gap) < org.gapMinutes;

  return (
    <div className="space-y-4" data-call-limits="assistant">
      <p className="text-sm text-muted-foreground">
        How often this assistant may call one phone number, counting only its own calls the person
        picks up. The organization's limits apply as well, to every assistant together.
        Leave a field empty for no extra limit.
      </p>
      {org.loaded && (
        <p className="text-sm text-muted-foreground" data-call-limits="ceiling">
          Organization limit: {org.perDay} {org.perDay === 1 ? "call" : "calls"} per number per day,{" "}
          {org.gapMinutes === 0 ? "no gap" : `${org.gapMinutes} minutes apart`}.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name="max_calls_per_number_per_day"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Calls per number per day</FormLabel>
              <FormControl>
                <Input
                  id="agent-calls-per-day"
                  inputMode="numeric"
                  placeholder="No assistant limit"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormDescription>{PER_DAY_MIN} to {PER_DAY_MAX}, a calendar day</FormDescription>
              {perDayLooser && (
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  Higher than the organization's {org.perDay}, so the organization's limit applies.
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="min_call_gap_minutes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Minutes between calls</FormLabel>
              <FormControl>
                <Input
                  id="agent-call-gap"
                  inputMode="numeric"
                  placeholder="No assistant limit"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormDescription>{GAP_MIN} to {GAP_MAX}; 0 is no gap</FormDescription>
              {gapLooser && (
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  Shorter than the organization's {org.gapMinutes} minutes, so the organization's gap applies.
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
